import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react'
import PropTypes from 'prop-types'
import Alert from '../alert'
import Icon from '../icon'
import classnames from 'classnames/bind'
import cls from './styles.scss'

const cx = classnames.bind(cls)
const SECONDS_TO_FADE = 0.3

/**
 * BannerNotification displays a banner notification in line in a page or
 * as an overlay.
 *
 * Ref is used to call open() or close() which makes it more adaptable to
 * useApplicationNotifiers hook (but could be used without it).
 */
const BannerNotification = forwardRef(({ isOverlay }, ref) => {
  const [state, setState] = useState({
    isVisible: false,
    FadeInOut: false,
    message: '',
    title: '',
    role: '',
    dismissible: false,
    showIcon: false,
    iconName: '',
  })

  let cleanupTimeout = null
  let fadeTimeout = null

  useEffect(() => {
    return () => {
      clearTimeout(cleanupTimeout)
      clearTimeout(fadeTimeout)
    }
  }, [cleanupTimeout, fadeTimeout])

  const open = options => {
    setState(prevState => ({
      ...prevState,
      isVisible: true,
      ...options,
    }))

    // wait a tick to allow the component to render before fading in
    setTimeout(() => {
      setState(prevState => ({
        ...prevState,
        FadeInOut: true,
      }))
    }, 0)

    if (options.autoDismiss) {
      cleanupTimeout && clearTimeout(cleanupTimeout)
      cleanupTimeout = setTimeout(close, options.duration)
    }
  }

  const resetState = () => {
    setState(prevState => ({ ...prevState, isVisible: false }))
  }

  const close = () => {
    setState(prevState => ({ ...prevState, FadeInOut: false }))
    fadeTimeout = setTimeout(() => {
      resetState()
      clearTimeout(fadeTimeout)
    }, SECONDS_TO_FADE * 1000)
  }

  const {
    isVisible,
    FadeInOut,
    message,
    title,
    role,
    dismissible,
    showIcon,
    iconName,
    width, // optional - see comment below
  } = state

  useImperativeHandle(ref, () => ({
    open,
    close,
  }))

  const renderIcon = iconName =>
    iconName && (
      <Icon name={iconName} color={role} iconStyle='light' size='big' />
    )

  const dataElmId = `application_notification_${
    isOverlay ? 'overlay' : 'page'
  }_banner`

  /**
   * This is a convenience so that the caller of the overlay banner doesn't necessarily have to
   * specify the width of the banner.  If the caller doesn't specify the width, we'll use the
   * width of the page layout (or any page element that includes data-page-layout attribute.)
   */
  const pageLayoutWidth = document
    ?.querySelector('[data-page-width]')
    ?.getAttribute('data-page-width')
  const newWidth = width || pageLayoutWidth

  return (
    <div
      className={cx(
        {
          'overlay-banner-container': isOverlay,
          hidden: !isVisible,
          visible: isVisible,
        },
        newWidth && `width-${newWidth}`
      )}
    >
      <Alert
        role={role}
        header={title}
        content={message}
        onDismiss={dismissible ? close : null}
        showIcon={iconName || showIcon}
        icon={renderIcon(iconName)}
        className={cx({
          'overlay-banner': isOverlay,
          'inline-banner': !isOverlay,
          hidden: !isVisible,
          visible: isVisible,
          'fade-in': FadeInOut,
        })}
        data-elm-id={dataElmId}
      />
    </div>
  )
})

BannerNotification.propTypes = {
  isOverlay: PropTypes.bool,
}

export default BannerNotification
